import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, UserPlus, Pencil, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  master_admin: 'bg-red-100 text-red-800',
  admin: 'bg-orange-100 text-orange-800',
  vp_operations: 'bg-violet-100 text-violet-800',
  manager: 'bg-purple-100 text-purple-800',
  trainer: 'bg-amber-100 text-amber-800',
  agent: 'bg-blue-100 text-blue-800',
};

function formatRole(role) {
  if (role === 'vp_operations') return 'VP - Operations';
  return role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
}

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [form, setForm] = useState({ username: '', name: '', password: '', role: 'agent' });
  const [editForm, setEditForm] = useState({ name: '', role: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');

  const isAdmin = ['master_admin', 'admin'].includes(user?.role);
  const isTrainer = user?.role === 'trainer';
  const canAssign = ['master_admin', 'admin', 'manager'].includes(user?.role);

  const fetchUsers = useCallback(async () => {
    try {
      // Admin sees all, trainer sees only assigned agents, others use view=all
      const params = isAdmin ? '' : isTrainer ? '' : '?view=all';
      const { data } = await axios.get(`${API}/users${params}`);
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isTrainer]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/users`, form);
      toast.success('User created successfully');
      setShowCreate(false);
      setForm({ username: '', name: '', password: '', role: 'agent' });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, password: '' });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setSubmitting(true);
    try {
      const payload = {};
      if (editForm.name && editForm.name !== editUser.name) payload.name = editForm.name;
      if (editForm.role && editForm.role !== editUser.role) payload.role = editForm.role;
      if (editForm.password.trim()) payload.password = editForm.password;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setShowEdit(false);
        return;
      }
      await axios.put(`${API}/users/${editUser.id}`, payload);
      toast.success('User updated successfully');
      setShowEdit(false);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const openDelete = (u) => {
    setDeleteTarget(u);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} has been deleted`);
      setShowDelete(false);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const openAssign = async (agent) => {
    setSelectedAgent(agent);
    try {
      const endpoint = isAdmin ? `${API}/users?role=trainer` : `${API}/users?view=all`;
      const { data } = await axios.get(endpoint);
      setTrainers(isAdmin ? data : data.filter(u => u.role === 'trainer'));
    } catch (e) { console.debug('openAssign trainers fetch skipped:', e?.response?.status); }
    setSelectedTrainer(agent.assigned_trainer_id || '');
    setShowAssign(true);
  };

  const handleAssign = async () => {
    if (!selectedTrainer || !selectedAgent) return;
    try {
      await axios.put(`${API}/users/${selectedAgent.id}/assign-trainer`, { trainer_id: selectedTrainer });
      toast.success('Trainer assigned');
      setShowAssign(false);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to assign trainer');
    }
  };

  const availableRoles = user?.role === 'master_admin'
    ? ['master_admin', 'admin', 'vp_operations', 'manager', 'trainer', 'agent']
    : ['admin', 'vp_operations', 'manager', 'trainer', 'agent'];

  const filteredUsers = users
    .filter(u => filterRole === 'all' || u.role === filterRole)
    .filter(u =>
      search === '' ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.username.toLowerCase().includes(search.toLowerCase())
    );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0047FF] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] tracking-tight">Users</h1>
          <p className="font-body text-sm text-[#4B5563] mt-1">{users.length} total users</p>
        </div>
        {isAdmin && (
          <Button data-testid="create-user-button" onClick={() => setShowCreate(true)} className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body gap-2">
            <Plus size={16} /> Create User
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative max-w-xs flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input data-testid="users-search" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-none border-[#E5E7EB] font-body" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['all', 'master_admin', 'admin', 'vp_operations', 'manager', 'trainer', 'agent'].map(r => (
            <button key={r} data-testid={`filter-${r}`} onClick={() => setFilterRole(r)}
              className={`px-3 py-1.5 text-xs font-body font-semibold uppercase tracking-wider border transition-colors ${filterRole === r ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}>
              {r === 'all' ? 'All' : formatRole(r)}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Name</th>
              <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Username</th>
              <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Role</th>
              <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Trainer</th>
              <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Status</th>
              {(isAdmin || canAssign) && (
                <th className="py-3 px-4 text-right font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.id} className={`border-b border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors ${u.journey_status === 'terminated' ? 'bg-red-50/50' : ''}`} data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-4 font-body text-sm font-medium text-[#0A0A0A]">{u.name}</td>
                <td className="py-3 px-4 font-body text-sm text-[#4B5563]">@{u.username}</td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-2 py-0.5 text-xs font-body font-semibold ${ROLE_COLORS[u.role] || ''}`}>{formatRole(u.role)}</span>
                </td>
                <td className="py-3 px-4 font-body text-sm text-[#4B5563]">{u.role === 'agent' ? (u.assigned_trainer_name || 'Unassigned') : '-'}</td>
                <td className="py-3 px-4">
                  {u.role === 'agent' && u.journey_status === 'terminated' ? (
                    <span className="inline-block px-2 py-0.5 text-xs font-body font-semibold bg-red-100 text-red-800">Terminated</span>
                  ) : u.role === 'agent' ? (
                    <span className="inline-block px-2 py-0.5 text-xs font-body font-semibold bg-emerald-100 text-emerald-800">Active</span>
                  ) : (
                    <span className="font-body text-sm text-[#4B5563]">-</span>
                  )}
                </td>
                {(isAdmin || canAssign) && (
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && (
                        <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 text-[#4B5563] hover:text-[#0047FF] transition-colors" title="Edit">
                          <Pencil size={16} />
                        </button>
                      )}
                      {u.role === 'agent' && canAssign && (
                        <button data-testid={`assign-trainer-${u.id}`} onClick={() => openAssign(u)} className="p-1.5 text-[#4B5563] hover:text-[#0047FF] transition-colors" title="Assign Trainer">
                          <UserPlus size={16} />
                        </button>
                      )}
                      {isAdmin && u.id !== user?.id && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => openDelete(u)} className="p-1.5 text-[#4B5563] hover:text-[#DC2626] transition-colors" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={isAdmin || canAssign ? 6 : 5} className="py-8 text-center font-body text-sm text-[#4B5563]">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* SweetAlert-style Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="rounded-none border-[#E5E7EB] max-w-md">
          <AlertDialogHeader>
            <div className="mx-auto w-14 h-14 bg-red-100 flex items-center justify-center mb-3">
              <AlertTriangle size={28} className="text-[#DC2626]" />
            </div>
            <AlertDialogTitle className="font-heading text-xl font-bold text-[#0A0A0A] text-center">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="font-body text-sm text-[#4B5563] text-center">
              Are you sure you want to delete <strong className="text-[#0A0A0A]">{deleteTarget?.name}</strong> (@{deleteTarget?.username})?
              <br />This will permanently remove the user and all their training data.
              <br /><span className="text-[#DC2626] font-semibold">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-3 mt-2">
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-none font-body border-[#E5E7EB]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white font-body">
              Yes, Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-none border-[#E5E7EB] shadow-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0A0A0A]">Create New User</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#4B5563]">Fill in the details to create a new user account.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Username</Label>
              <Input data-testid="create-username-input" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} className="mt-1 rounded-none" required />
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Full Name</Label>
              <Input data-testid="create-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 rounded-none" required />
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Password</Label>
              <Input data-testid="create-password-input" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="mt-1 rounded-none" required />
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="create-role-select" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{availableRoles.map(r => (<SelectItem key={r} value={r} className="font-body text-sm">{formatRole(r)}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-none font-body">Cancel</Button>
              <Button data-testid="create-user-submit" type="submit" disabled={submitting} className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body">{submitting ? 'Creating...' : 'Create User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="rounded-none border-[#E5E7EB] shadow-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0A0A0A]">Edit User</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#4B5563]">Update profile for @{editUser?.username}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Full Name</Label>
              <Input data-testid="edit-name-input" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="mt-1 rounded-none" required />
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="edit-role-select" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{availableRoles.map(r => (<SelectItem key={r} value={r} className="font-body text-sm">{formatRole(r)}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">New Password</Label>
              <Input data-testid="edit-password-input" type="password" value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} className="mt-1 rounded-none" placeholder="Leave blank to keep current" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)} className="rounded-none font-body">Cancel</Button>
              <Button data-testid="edit-user-submit" type="submit" disabled={submitting} className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body">{submitting ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Trainer Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="rounded-none border-[#E5E7EB] shadow-lg max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#0A0A0A]">Assign Trainer</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#4B5563]">Select a trainer to assign to this agent.</DialogDescription>
          </DialogHeader>
          <p className="font-body text-sm text-[#4B5563] mb-4">Assign a trainer to <strong>{selectedAgent?.name}</strong></p>
          <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
            <SelectTrigger data-testid="assign-trainer-select" className="rounded-none"><SelectValue placeholder="Select a trainer" /></SelectTrigger>
            <SelectContent className="rounded-none">{trainers.map(t => (<SelectItem key={t.id} value={t.id} className="font-body text-sm">{t.name}</SelectItem>))}</SelectContent>
          </Select>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAssign(false)} className="rounded-none font-body">Cancel</Button>
            <Button data-testid="assign-trainer-submit" onClick={handleAssign} disabled={!selectedTrainer} className="rounded-none bg-[#0047FF] hover:bg-[#0036CC] text-white font-body">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
